/**
 * B1078 — Journal des envois de rapports (Eric 2026-09-05 SPEC STRICK PROD).
 *
 * Partagé par la page Sentinelle (rapport de fiabilité) et la page Forfait AI
 * (rapport de consommation) : les deux pages posaient la même question — « le
 * rapport est-il vraiment parti, et à qui ? ». Les envois sautés et refusés
 * sont affichés eux aussi : un trou inexpliqué serait pire qu'une ligne
 * « non envoyé ».
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ClockCounterClockwise, DownloadSimple } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  // Tâche 1420 — États du journal : texte d'interface, donc bilingue.
  sent: { fr: "Envoyé", en: "Sent", className: "bg-emerald-50 text-emerald-700" },
  failed: { fr: "Refusé", en: "Refused", className: "bg-red-50 text-red-700" },
  skipped: { fr: "Non envoyé", en: "Not sent", className: "bg-zinc-100 text-zinc-600" },
};

const FREQ_LABELS = {
  daily: { fr: "Chaque jour", en: "Every day" },
  weekly: { fr: "Chaque semaine", en: "Every week" },
  biweekly: { fr: "Aux 2 semaines", en: "Every 2 weeks" },
  monthly: { fr: "Chaque mois", en: "Every month" },
  quarterly: { fr: "Chaque trimestre", en: "Every quarter" },
};

// Tâche 1431 — Les motifs sont ÉCRITS EN BASE au moment de l'envoi, en
// français : on ne peut pas les réécrire sans réécrire l'historique. On les
// traduit donc à l'affichage, et un motif inconnu reste en français — un texte
// compréhensible vaut mieux qu'une case vide.
const MOTIFS_EN = {
  "courriels désactivés sur cet environnement": "emails disabled on this environment",
  "envoi refusé par le fournisseur": "send refused by the provider",
  "rapport désactivé": "report disabled",
  "aucun destinataire sélectionné": "no recipient selected",
  "aucun destinataire vérifié": "no verified recipient",
};

export const ReportSendLog = ({ endpoint, testid, showAmount = false, reloadKey = 0 }) => {
  // Tâche 1420 — Sous-composant : il a son propre hook de langue.
  const { tr, lang } = useLang();
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [endpoint, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  // B1131 — Export CSV : le tableau à l'écran ne se copie pas proprement
  // au-delà de quelques lignes, et Eric doit pouvoir archiver le journal.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}${endpoint}/csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${testid}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (!data) return null;
  const entries = data.entries || [];

  return (
    <div className="mt-6" data-testid={testid}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
          <ClockCounterClockwise size={14} />
          {tr("Journal des envois", "Delivery log")}
          <span className="font-normal normal-case tracking-normal text-zinc-400">
            {tr(
            `— conservé ${Math.round((data.retention_days || 730) / 30)} mois`,
            `— kept for ${Math.round((data.retention_days || 730) / 30)} months`,
          )}
          </span>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={exporting || !entries.length}
          title={
            entries.length
              ? tr("Télécharger le journal en CSV (Excel)", "Download the log as CSV (Excel)")
              : tr("Aucun envoi à exporter", "Nothing to export")
          }
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-zinc-300 text-xs text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-40 disabled:hover:bg-transparent"
          data-testid={`${testid}-export-csv`}
        >
          <DownloadSimple size={14} />
          {exporting ? tr("Export…", "Exporting…") : tr("Exporter CSV", "Export CSV")}
        </button>
      </div>
      {!entries.length ? (
        <p className="text-xs text-zinc-500" data-testid={`${testid}-empty`}>
          {tr("Aucun envoi enregistré pour cette organisation.", "No delivery recorded for this organisation.")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200">
          <table className="w-full text-left text-xs" data-testid={`${testid}-table`}>
            <thead className="bg-zinc-50 text-zinc-500">
              <tr>
                <th className="py-2 px-3 font-semibold">Date</th>
                <th className="py-2 px-3 font-semibold">{tr("Périodicité", "Frequency")}</th>
                <th className="py-2 px-3 font-semibold">{tr("Destinataires", "Recipients")}</th>
                {showAmount && <th className="py-2 px-3 font-semibold text-right">{tr("Montant", "Amount")}</th>}
                <th className="py-2 px-3 font-semibold">{tr("État", "Status")}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const status = STATUS[e.status] || STATUS.skipped;
                const people = [...(e.recipients || []), ...(e.failed_recipients || [])];
                return (
                  <tr
                    key={e.id}
                    className="border-t border-zinc-100"
                    data-testid={`${testid}-row-${e.id}`}
                  >
                    <td className="py-2 px-3 text-zinc-700 whitespace-nowrap">
                      {/* Tâche 1431 — La date suivait la langue du Québec
                          quelle que soit la langue choisie : « 5 sept. 2026,
                          13 h 22 » dans une interface anglaise. Le fuseau
                          reste Montréal — c'est le fuseau de l'entreprise,
                          pas une préférence de lecture. */}
                      {new Date(e.sent_at).toLocaleString(lang === "en" ? "en-CA" : "fr-CA", {
                        dateStyle: "medium",
                        timeStyle: "short",
                        timeZone: "America/Toronto",
                      })}
                      {e.test && (
                        <span
                          className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700"
                          data-testid={`${testid}-test-${e.id}`}
                        >
                          test
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-zinc-600 whitespace-nowrap">
                      {(FREQ_LABELS[e.frequency] && (lang === "en" ? FREQ_LABELS[e.frequency].en : FREQ_LABELS[e.frequency].fr)) || e.frequency || "—"}
                    </td>
                    <td className="py-2 px-3 text-zinc-600">
                      {people.length ? people.join(", ") : "—"}
                    </td>
                    {showAmount && (
                      <td className="py-2 px-3 text-zinc-700 text-right whitespace-nowrap">
                        {e.total_cad === null || e.total_cad === undefined
                          ? "—"
                          : `${Number(e.total_cad).toFixed(2)} $`}
                      </td>
                    )}
                    <td className="py-2 px-3 whitespace-nowrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${status.className}`}>
                        {lang === "en" ? status.en : status.fr}
                      </span>
                      {e.reason && (
                        <span className="block text-[10px] text-zinc-400 mt-0.5">
                          {lang === "en" ? (MOTIFS_EN[e.reason] || e.reason) : e.reason}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportSendLog;
